<template>
  <div class="collect-timeline-page">
    <!-- 查询区 -->
    <el-card shadow="never" class="query-card">
      <div class="query-bar">
        <div class="query-left">
          <el-date-picker
            v-model="queryRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="timestamp"
            format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            class="date-range"
          />

          <el-input
            v-model.trim="query.community_name"
            placeholder="请输入小区名称"
            clearable
            class="query-input"
          />

          <el-input
            v-model.trim="query.device_id"
            placeholder="请输入设备ID"
            clearable
            class="query-input small"
          />

          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="fetchData">
            查询
          </el-button>

          <el-button icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </div>

        <div class="query-right">
          <el-button
            size="mini"
            :type="quickRangeType === 'all' ? 'primary' : 'default'"
            @click="setQuickRange('all')"
          >
            全部
          </el-button>
          <el-button
            size="mini"
            :type="quickRangeType === 'month' ? 'primary' : 'default'"
            @click="setQuickRange('month')"
          >
            近一月
          </el-button>
          <el-button
            size="mini"
            :type="quickRangeType === '7days' ? 'primary' : 'default'"
            @click="setQuickRange('7days')"
          >
            近7天
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 汇总 -->
    <div class="summary-cards">
      <div class="summary-card">
        <div class="label">小区总数</div>
        <div class="value">{{ summary.total_communities }}</div>
      </div>
      <div class="summary-card">
        <div class="label">总事件数</div>
        <div class="value">{{ summary.total_records }}</div>
      </div>
    </div>

    <!-- 小区卡片列表 -->
    <el-card shadow="never" class="list-card">
      <div slot="header" class="card-header">
        <span>小区列表</span>
        <span class="header-right">共 {{ communityCards.length }} 个小区</span>
      </div>

      <div v-if="loading" class="loading-wrap">
        <i class="el-icon-loading"></i>
        <span>数据加载中...</span>
      </div>

      <div v-else-if="communityCards.length" class="community-grid">
        <div
          v-for="(item, index) in communityCards"
          :key="`${item.community_name}-${item.device_code || ''}-${index}`"
          class="community-card"
        >
          <div class="community-top">
            <div class="community-name" :title="item.address">
              {{ item.address || '-' }}
            </div>
            <el-tag size="mini" type="primary">
              {{ item.record_count || 0 }} 条事件
            </el-tag>
          </div>

          <div class="community-info">
            <div class="info-row">
              <span class="label">设备编码</span>
              <span class="value">{{ item.device_code || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="label">快照重量</span>
              <span class="value">{{ formatWeight(item.collect_weight) }} kg</span>
            </div>
            <div class="info-row">
              <span class="label">已审核重量</span>
              <span class="value">{{ formatWeight(item.audited_weight) }} kg</span>
            </div>
            <div class="info-row">
              <span class="label">过磅重量</span>
              <span class="value">{{ formatWeight(item.weigh_weight) }} kg</span>
            </div>
            <div class="info-row">
              <span class="label">审核差值</span>
              <span class="value" :class="getDiffClass(item.audited_diff_weight)">
                {{ formatWeight(item.audited_diff_weight) }} kg
              </span>
            </div>
          </div>

          <div class="community-footer">
            <el-button type="primary" size="mini" @click="openDetail(item)">
              详情
            </el-button>
          </div>
        </div>
      </div>

      <div v-else class="empty-wrap">
        暂无小区数据
      </div>
    </el-card>

    <!-- 时间线弹窗 -->
    <el-dialog
      :visible.sync="detailVisible"
      width="900px"
      :close-on-click-modal="false"
      append-to-body
      custom-class="timeline-dialog"
    >
      <div slot="title" class="dialog-title">
        <span>{{ currentCommunity.address || '小区详情' }}</span>
      </div>

      <div class="dialog-summary">
        <div class="summary-item">
          <span class="s-label">设备ID：</span>
          <span class="s-value">{{ currentCommunity.device_id || '-' }}</span>
        </div>
        <div class="summary-item">
          <span class="s-label">设备编码：</span>
          <span class="s-value">{{ currentCommunity.device_code || '-' }}</span>
        </div>
        <div class="summary-item">
          <span class="s-label">地址：</span>
          <span class="s-value">{{ currentCommunity.address || '-' }}</span>
        </div>
        <div class="summary-item">
          <span class="s-label">事件数：</span>
          <span class="s-value">{{ (currentCommunity.events || []).length }}</span>
        </div>
      </div>

      <div v-if="currentCommunity.events && currentCommunity.events.length" class="timeline-wrap">
        <el-timeline>
          <el-timeline-item
            v-for="(event, index) in sortedEvents"
            :key="index"
            :timestamp="getTimelineTimestamp(event)"
            :type="getTimelineType(event.audit_status)"
            placement="top"
          >
            <div class="timeline-card">
              <div class="timeline-head">
                <div class="vehicle-name">{{ event.vehicle_name || '-' }}</div>
                <el-tag :type="getAuditTagType(event.audit_status)" size="mini">
                  {{ getAuditStatusText(event.audit_status) }}
                </el-tag>
              </div>

              <div class="timeline-grid">
                <div class="t-row">
                  <span class="k">清运开始</span>
                  <span class="v">{{ formatTimestamp(event.collect_start_time) }}</span>
                </div>
                <div class="t-row">
                  <span class="k">清运结束</span>
                  <span class="v">{{ formatTimestamp(event.collect_end_time) }}</span>
                </div>
                <div class="t-row">
                  <span class="k">快照重量</span>
                  <span class="v">{{ formatWeight(event.collect_room_weight) }} kg</span>
                </div>
                <div class="t-row">
                  <span class="k">过磅单号</span>
                  <span class="v">{{ event.weigh_no || '-' }}</span>
                </div>
                <div class="t-row">
                  <span class="k">过磅重量</span>
                  <span class="v">{{ formatWeight(event.weigh_weight) }} kg</span>
                </div>
                <div class="t-row">
                  <span class="k">最终订单重量</span>
                  <span class="v">{{ formatWeight(event.final_room_weight) }} kg</span>
                </div>
                <div class="t-row">
                  <span class="k">最终差值</span>
                  <span class="v" :class="getDiffClass(event.final_diff_weight)">
                    {{ formatWeight(event.final_diff_weight) }} kg
                  </span>
                </div>
              </div>
            </div>
          </el-timeline-item>
        </el-timeline>
      </div>

      <div v-else class="empty-wrap dialog-empty">
        暂无时间线事件
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'CollectTimeline',
  data() {
    return {
      loading: false,
      queryRange: [],
      quickRangeType: '7days',
      query: {
        community_name: '',
        device_id: ''
      },
      summary: {
        total_communities: 0,
        total_records: 0
      },
      items: [],
      communitySummary: [],
      detailVisible: false,
      currentCommunity: {}
    }
  },

  computed: {
    communityCards() {
      return Array.isArray(this.communitySummary) ? this.communitySummary : []
    },

    sortedEvents() {
      const list = Array.isArray(this.currentCommunity.events)
        ? [...this.currentCommunity.events]
        : []
      return list.sort((a, b) => {
        return Number(a.collect_start_time || 0) - Number(b.collect_start_time || 0)
      })
    }
  },

  created() {
    this.setQuickRange('7days', false)
    this.fetchData()
  },

  methods: {
    initDefaultRange() {
      this.setQuickRange('7days', false)
    },

    setQuickRange(type, needFetch = true) {
      this.quickRangeType = type
      const now = new Date()
      let start = null
      let end = new Date(now)

      if (type === '7days') {
        end.setHours(23, 59, 59, 999)
        start = new Date(now)
        start.setHours(0, 0, 0, 0)
        start.setDate(start.getDate() - 6)
        this.queryRange = [start.getTime(), end.getTime()]
      } else if (type === 'month') {
        end.setHours(23, 59, 59, 999)
        start = new Date(now)
        start.setHours(0, 0, 0, 0)
        start.setDate(start.getDate() - 29)
        this.queryRange = [start.getTime(), end.getTime()]
      } else if (type === 'all') {
        start = new Date('2023-01-01 00:00:00')
        this.queryRange = [start.getTime(), now.getTime()]
      }

      if (needFetch) {
        this.fetchData()
      }
    },

    async fetchData() {
      const params = {}

      if (this.queryRange && this.queryRange.length === 2) {
        params.start_time = Math.floor(this.queryRange[0] / 1000)
        params.end_time = Math.floor(this.queryRange[1] / 1000)
      }

      if (this.query.community_name) {
        params.community_name = this.query.community_name
      }

      if (this.query.device_id !== '' && this.query.device_id !== null && this.query.device_id !== undefined) {
        params.device_id = this.query.device_id
      }

      this.loading = true
      try {
        const res = await request({
          url: '/wms/report/timeline',
          method: 'get',
          params
        })

        const data = this.extractData(res)

        this.summary = Object.assign(
          {
            total_communities: 0,
            total_records: 0
          },
          data.summary || {}
        )

        this.items = Array.isArray(data.items) ? data.items : []
        this.communitySummary = Array.isArray(data.community_summary) ? data.community_summary : []
      } catch (error) {
        console.error('获取小区时间线数据失败', error)
        this.$message.error('获取小区时间线数据失败')
      } finally {
        this.loading = false
      }
    },

    extractData(res) {
      const data = res && res.data ? res.data : res
      if (data && data.data) return data.data
      return data || {}
    },

    handleReset() {
      this.query.community_name = ''
      this.query.device_id = ''
      this.setQuickRange('7days')
    },

    openDetail(item) {
      const detail = (this.items || []).find(v => {
        return (
          String(v.community_name || '') === String(item.community_name || '') &&
          String(v.device_code || '') === String(item.device_code || '')
        )
      })

      this.currentCommunity = detail
        ? Object.assign({}, detail)
        : {
            community_name: item.community_name,
            device_id: item.device_id,
            device_code: item.device_code,
            address: item.address,
            events: []
          }

      this.detailVisible = true
    },

    formatWeight(val) {
      const num = Number(val || 0)
      if (Number.isNaN(num)) return '0.00'
      return num.toFixed(2)
    },

    formatTimestamp(ts) {
      if (!ts) return '-'
      const date = new Date(Number(ts) * 1000)
      if (Number.isNaN(date.getTime())) return '-'

      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const i = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${i}:${s}`
    },

    getTimelineTimestamp(event) {
      const start = this.formatTimestamp(event.collect_start_time)
      const end = this.formatTimestamp(event.collect_end_time)
      return `开始：${start}  ｜  结束：${end}`
    },

    getAuditStatusText(status) {
      const map = {
        0: '未审核',
        1: '已通过',
        2: '已驳回'
      }
      return map[status] || '未知'
    },

    getAuditTagType(status) {
      const map = {
        0: 'warning',
        1: 'success',
        2: 'danger'
      }
      return map[status] || ''
    },

    getTimelineType(status) {
      const map = {
        0: 'warning',
        1: 'success',
        2: 'danger'
      }
      return map[status] || 'primary'
    },

    getDiffClass(diff) {
      const num = Number(diff || 0)
      if (num > 0) return 'text-danger'
      if (num < 0) return 'text-success'
      return ''
    }
  }
}
</script>

<style scoped>
.collect-timeline-page {
  min-height: 100%;
}

.query-card {
  margin-bottom: 12px;
  border-radius: 14px;
}

.query-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.query-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.query-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.date-range {
  width: 420px;
}

.query-input {
  width: 220px;
}

.query-input.small {
  width: 150px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.summary-card {
  background: linear-gradient(135deg, #0f172a 0%, #18243c 100%);
  border-radius: 14px;
  padding: 18px 18px;
  color: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.summary-card .label {
  font-size: 13px;
  color: rgba(255,255,255,0.72);
  margin-bottom: 8px;
}

.summary-card .value {
  font-size: 28px;
  font-weight: 700;
}

.list-card {
  border-radius: 14px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-right {
  color: #64748b;
  font-size: 13px;
}

.community-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.community-card {
  border: 1px solid #e7edf7;
  border-radius: 14px;
  padding: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);
  transition: all 0.2s;
}

.community-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.community-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.community-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.community-info {
  margin-bottom: 14px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed #edf2f7;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  color: #64748b;
  font-size: 13px;
}

.info-row .value {
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  text-align: right;
  word-break: break-all;
}

.info-row .value.address {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.community-footer {
  display: flex;
  justify-content: flex-end;
}

.loading-wrap,
.empty-wrap {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
  gap: 8px;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.dialog-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  padding: 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
}

.summary-item {
  min-width: 0;
}

.s-label {
  color: #64748b;
  font-size: 13px;
}

.s-value {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  word-break: break-all;
}

.timeline-wrap {
  max-height: 560px;
  overflow-y: auto;
  padding-right: 8px;
}

.timeline-card {
  background: #f8fbff;
  border: 1px solid #e8eef8;
  border-radius: 12px;
  padding: 14px;
}

.timeline-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.vehicle-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.timeline-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 18px;
}

.t-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px dashed #e5e7eb;
  padding-bottom: 8px;
}

.t-row .k {
  color: #64748b;
  font-size: 13px;
}

.t-row .v {
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  text-align: right;
  word-break: break-all;
}

.text-danger {
  color: #ef4444 !important;
}

.text-success {
  color: #10b981 !important;
}

.dialog-empty {
  min-height: 240px;
}

@media screen and (max-width: 1600px) {
  .community-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .dialog-summary {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 1366px) {
  .community-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .timeline-grid {
    grid-template-columns: 1fr;
  }
}
</style>
